import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Heart, Users, HandHeart, Award } from 'lucide-react';
import { PublicLayout } from '@/components/PublicLayout';

const About = () => {
  const { t } = useLanguage();

  const values = [
    {
      icon: Users,
      title: t.about.proximity,
      description: t.about.proximityText,
    },
    {
      icon: HandHeart,
      title: t.about.solidarity,
      description: t.about.solidarityText,
    },
    {
      icon: Heart,
      title: t.about.love,
      description: t.about.loveText,
    },
    {
      icon: Award,
      title: t.about.respect,
      description: t.about.respectText,
    },
  ];

  return (
    <PublicLayout>
      {/* Header */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6 text-center">
            {t.about.title}
          </h1>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div data-testid="mission-image">
              <img
                src="/images/lyon-city.jpg"
                alt="Lyon"
                className="rounded-2xl shadow-xl w-full h-96 object-cover"
              />
            </div>
            <div data-testid="mission-content">
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t.about.mission}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                {t.about.missionText}
              </p>
              <div className="bg-primary-50 border-l-4 border-[#127abb] p-6 rounded-lg">
                <p className="text-slate-700 font-medium">
                  « Ensemble pour une aide efficace, durable et au bon moment ! »
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              {t.about.values}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  data-testid={`value-card-${index}`}
                  className="bg-white border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 p-8 rounded-2xl text-center"
                >
                  <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="text-[#127abb]" size={32} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#127abb] to-[#0e6292] text-white rounded-3xl p-12 text-center shadow-xl">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.about.pricing}</h2>
              <div className="text-6xl font-bold mb-4">20€</div>
              <p className="text-xl text-primary-50 mb-8">{t.about.pricingText}</p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>Recherche de logement</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>Accompagnement administratif</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>Recherche de job</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>Intégration</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default About;